import { useEffect, useId, useRef, useState, type RefObject } from "react"
import { createPortal } from "react-dom"
import { Check, Scissors, Trash2 } from "lucide-react"
import { MENU_Z_INDEX, useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { useTranslation } from "@/hooks/use-translation"
import { MAX_PATH_DURATION_MS } from "@/lib/location-scene-3d/camera-path"
import { MIN_SPLINE_DURATION_MS } from "@/lib/location-scene-3d/camera-path-spline"
import {
  MIN_SPEED_CUT_GAP_MS,
  SPEED_PRESETS,
  SPEED_Y_MAX,
  addSpeedKnot,
  applySpeedPreset,
  curveSamples,
  displaySpeedProfile,
  moveSpeedCut,
  moveSpeedKnots,
  normalizeSpeedProfile,
  presetSpeedSegment,
  removeSpeedCut,
  removeSpeedKnot,
  segmentSpeedAt,
  speedSectionStats,
  speedStrokePoints,
  type CameraSpeedProfile,
  type SpeedPresetName,
  type SpeedSectionStats,
} from "@/lib/location-scene-3d/camera-speed"
import { cn } from "@/lib/utils"

const RULER_DRAG_PX = 4

type LocationSceneSpeedEditorProps = {
  durationMs: number
  speed: CameraSpeedProfile | null | undefined
  playheadRef: RefObject<HTMLDivElement | null>
  onSeek: (ms: number) => void
  onChange: (speed: CameraSpeedProfile) => void
  onDuration: (ms: number) => void
  onSplit: () => void
}

type KnotSelection = { segment: number; knot: number }

type KnotDragItem = KnotSelection & { u: number; y: number; t0: number; t1: number }

type MarqueeRect = { left: number; top: number; width: number; height: number }

type Drag =
  | { kind: "pending"; index: number; u: number; onLine: boolean; clientX: number; clientY: number }
  | { kind: "marquee"; x0: number; y0: number; x1: number; y1: number }
  | { kind: "knots"; originX: number; originY: number; base: CameraSpeedProfile; items: KnotDragItem[] }
  | { kind: "cut"; index: number }

const MENU_ITEM =
  "flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-ui-text-primary outline-none hover:bg-ui-surface-sunken"

function clamp(v: number, a: number, b: number) {
  return Math.min(b, Math.max(a, v))
}

function formatTimecode(ms: number) {
  const seconds = Math.max(0, ms) / 1000
  return `${seconds < 10 ? "0" : ""}${seconds.toFixed(1)}`
}

function parseTimecode(text: string): number | null {
  const match = /^(\d+(?:\.\d+)?)$/.exec(text.trim().replace(",", "."))
  if (!match) return null
  const seconds = Number(match[1])
  if (!Number.isFinite(seconds) || seconds >= 60) return null
  return Math.round(seconds * 1000)
}

function speedToViewY(speedY: number) {
  return (1 - speedY / SPEED_Y_MAX) * 100
}

function viewFractionToSpeed(fractionFromTop: number) {
  return (1 - fractionFromTop) * SPEED_Y_MAX
}

function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
}

const SPEED_GUIDE_GRAY = "var(--ui-text-muted)"

const LINE_SLACK_PX = 8
const KNOT_HIT_PX = 6
const DRAG_SLOP_PX = 4

function sameKnot(a: KnotSelection, b: KnotSelection) {
  return a.segment === b.segment && a.knot === b.knot
}

function marqueeClientRect(x0: number, y0: number, x1: number, y1: number, bounds: DOMRect) {
  const left = clamp(Math.min(x0, x1), bounds.left, bounds.right)
  const right = clamp(Math.max(x0, x1), bounds.left, bounds.right)
  const top = clamp(Math.min(y0, y1), bounds.top, bounds.bottom)
  const bottom = clamp(Math.max(y0, y1), bounds.top, bounds.bottom)
  return { left, right, top, bottom }
}

function marqueeStyle(x0: number, y0: number, x1: number, y1: number, bounds: DOMRect): MarqueeRect {
  const box = marqueeClientRect(x0, y0, x1, y1, bounds)
  return {
    left: box.left - bounds.left,
    top: box.top - bounds.top,
    width: Math.max(box.right - box.left, 0),
    height: Math.max(box.bottom - box.top, 0),
  }
}

function distanceToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const abx = bx - ax
  const aby = by - ay
  const len2 = abx * abx + aby * aby
  if (len2 <= 1e-8) return Math.hypot(px - ax, py - ay)
  const t = clamp(((px - ax) * abx + (py - ay) * aby) / len2, 0, 1)
  return Math.hypot(px - (ax + abx * t), py - (ay + aby * t))
}

function sectionCurvePoints(section: SpeedSectionStats) {
  return speedStrokePoints(section.segment) ?? curveSamples(section.segment, 28)
}

function hitsCurve(clientX: number, clientY: number, rect: DOMRect, section: SpeedSectionStats, durationMs: number) {
  if (durationMs <= 0 || rect.width <= 0 || rect.height <= 0) return false
  const samples = sectionCurvePoints(section)
  const span = section.t1 - section.t0
  let best = Number.POSITIVE_INFINITY
  for (let i = 0; i < samples.length - 1; i += 1) {
    const a = samples[i]
    const b = samples[i + 1]
    const ax = rect.left + ((section.t0 + a.u * span) / durationMs) * rect.width
    const ay = rect.top + (speedToViewY(a.y) / 100) * rect.height
    const bx = rect.left + ((section.t0 + b.u * span) / durationMs) * rect.width
    const by = rect.top + (speedToViewY(b.y) / 100) * rect.height
    const dist = distanceToSegment(clientX, clientY, ax, ay, bx, by)
    if (dist < best) best = dist
  }
  return best <= LINE_SLACK_PX
}

function curvePath(points: { u: number; y: number }[]) {
  return points
    .map((knot, index) => {
      const x = (knot.u * 100).toFixed(2)
      const y = speedToViewY(knot.y).toFixed(2)
      return `${index === 0 ? "M" : "L"}${x} ${y}`
    })
    .join(" ")
}

const PRESET_THUMB_W = 28
const PRESET_THUMB_H = 16

function presetThumbPath(preset: SpeedPresetName) {
  const inset = 1
  return curveSamples(presetSpeedSegment(preset), 12)
    .map((knot, index) => {
      const x = inset + knot.u * (PRESET_THUMB_W - inset * 2)
      const y = inset + (1 - knot.y / SPEED_Y_MAX) * (PRESET_THUMB_H - inset * 2)
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(" ")
}

function SpeedPresetThumb({ preset }: { preset: SpeedPresetName }) {
  const hatchId = useId().replace(/:/g, "")
  if (preset === "pause") {
    return (
      <svg
        viewBox={`0 0 ${PRESET_THUMB_W} ${PRESET_THUMB_H}`}
        className="h-4 w-7 shrink-0 text-ui-text-muted/35"
        aria-hidden
      >
        <defs>
          <pattern id={hatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="2" />
          </pattern>
        </defs>
        <rect width={PRESET_THUMB_W} height={PRESET_THUMB_H} fill={`url(#${hatchId})`} />
      </svg>
    )
  }
  return (
    <svg viewBox={`0 0 ${PRESET_THUMB_W} ${PRESET_THUMB_H}`} className="h-4 w-7 shrink-0" aria-hidden>
      <path d={presetThumbPath(preset)} className="fill-none stroke-ui-accent" strokeWidth="1.5" />
    </svg>
  )
}

function curveFill(points: { u: number; y: number }[]) {
  if (points.length === 0) return ""
  const line = curvePath(points)
  const x0 = (points[0].u * 100).toFixed(2)
  const x1 = (points[points.length - 1].u * 100).toFixed(2)
  return `${line} L${x1} 100 L${x0} 100 Z`
}

export function LocationSceneSpeedEditor({
  durationMs,
  speed,
  playheadRef,
  onSeek,
  onChange,
  onDuration,
  onSplit,
}: LocationSceneSpeedEditorProps) {
  const { t } = useTranslation(["workspace"])
  const rootRef = useRef<HTMLDivElement>(null)
  const graphRef = useRef<HTMLDivElement>(null)
  const rulerRef = useRef<HTMLDivElement>(null)
  const rulerGestureRef = useRef<{ x: number; scrubbing: boolean; editIndex: number | null } | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<Drag | null>(null)
  const gestureRef = useRef("")
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const onDurationRef = useRef(onDuration)
  onDurationRef.current = onDuration
  const [draft, setDraft] = useState(() => displaySpeedProfile(speed))
  const draftRef = useRef(draft)
  draftRef.current = draft
  const [selected, setSelected] = useState<number | null>(0)
  const [knots, setKnots] = useState<KnotSelection[]>([])
  const knotsRef = useRef(knots)
  knotsRef.current = knots
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null)
  const [menu, setMenu] = useState<{ index: number; x: number; y: number } | null>(null)
  const [cutDrag, setCutDrag] = useState<number | null>(null)
  const [hoverCut, setHoverCut] = useState<number | null>(null)
  const [splitHover, setSplitHover] = useState(false)
  const [graphPx, setGraphPx] = useState({ w: 0, h: 0 })
  const [timeEdit, setTimeEdit] = useState<{ index: number; text: string; ms: number } | null>(null)
  const cancelTimeEditRef = useRef(false)

  useEffect(() => {
    if (dragRef.current) return
    setDraft(displaySpeedProfile(speed))
  }, [speed])

  useEffect(() => {
    if (selected == null && knots.length === 0) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (graphRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setSelected(null)
      setKnots([])
    }
    window.addEventListener("pointerdown", onPointerDown)
    return () => window.removeEventListener("pointerdown", onPointerDown)
  }, [selected, knots.length])

  useEffect(() => {
    const el = graphRef.current
    if (!el) return
    const apply = () => {
      const rect = el.getBoundingClientRect()
      setGraphPx((prev) => (prev.w === rect.width && prev.h === rect.height ? prev : { w: rect.width, h: rect.height }))
    }
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const stats = speedSectionStats(draft, durationMs)
  const edges = [0, ...draft.cuts, durationMs]

  const { menuStyle, portalRoot } = useDropdownMenu({
    open: menu != null,
    direction: "up",
    align: "left",
    anchorRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: "max-content", maxWidth: "min(16rem, calc(100vw - 16px))" }),
    onOutsideClick: () => setMenu(null),
    estimatedMenuHeightPx: 180,
    estimatedMenuWidthPx: 200,
    zIndex: MENU_Z_INDEX,
  })

  const commit = (next: CameraSpeedProfile) => {
    setDraft(next)
    draftRef.current = next
    onChangeRef.current(next)
  }

  const locals = (clientX: number, clientY: number) => {
    const rect = graphRef.current?.getBoundingClientRect()
    const sections = speedSectionStats(draftRef.current, durationMs)
    if (!rect || rect.width <= 0 || rect.height <= 0 || sections.length === 0) return null
    const x = clamp((clientX - rect.left) / rect.width, 0, 1)
    const y = clamp(viewFractionToSpeed(clamp((clientY - rect.top) / rect.height, 0, 1)), 0, SPEED_Y_MAX)
    const ms = x * durationMs
    let index = sections.length - 1
    for (let i = 0; i < sections.length; i += 1) {
      if (ms >= sections[i].t0 && ms <= sections[i].t1) {
        index = i
        break
      }
    }
    const section = sections[index]
    const span = section.t1 - section.t0
    const u = span <= 1e-6 ? 0 : clamp((ms - section.t0) / span, 0, 1)
    return { ms, y, index, u, rect }
  }

  const hitKnot = (clientX: number, clientY: number): KnotSelection | null => {
    const rect = graphRef.current?.getBoundingClientRect()
    const sections = speedSectionStats(draftRef.current, durationMs)
    if (!rect || durationMs <= 0) return null
    let best: KnotSelection | null = null
    let bestDist = Number.POSITIVE_INFINITY
    for (const section of sections) {
      section.segment.knots.forEach((point, knotIndex) => {
        const px = rect.left + ((section.t0 + point.u * (section.t1 - section.t0)) / durationMs) * rect.width
        const py = rect.top + (speedToViewY(point.y) / 100) * rect.height
        const dist = Math.hypot(px - clientX, py - clientY)
        if (dist <= KNOT_HIT_PX && dist < bestDist) {
          best = { segment: section.index, knot: knotIndex }
          bestDist = dist
        }
      })
    }
    return best
  }

  const hitCut = (clientX: number): number => {
    const rect = graphRef.current?.getBoundingClientRect()
    if (!rect || durationMs <= 0) return -1
    let best = -1
    let bestDist = 8
    draftRef.current.cuts.forEach((cut, index) => {
      const px = rect.left + (cut / durationMs) * rect.width
      const dist = Math.abs(px - clientX)
      if (dist <= bestDist) {
        best = index
        bestDist = dist
      }
    })
    return best
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return
      if (event.key === "Escape" && menu) {
        event.preventDefault()
        event.stopImmediatePropagation()
        setMenu(null)
        return
      }
      if (event.key !== "Backspace" && event.key !== "Delete") return
      const current = knotsRef.current
      if (current.length === 0) return
      const sorted = [...current].sort((a, b) => a.segment - b.segment || b.knot - a.knot)
      let profile = draftRef.current
      let changed = false
      for (const item of sorted) {
        const next = removeSpeedKnot(profile, item.segment, item.knot)
        if (!next) continue
        profile = next
        changed = true
      }
      if (!changed) return
      event.preventDefault()
      setKnots([])
      commit(profile)
    }
    window.addEventListener("keydown", onKey, true)
    return () => window.removeEventListener("keydown", onKey, true)
    // commit closes over onChange; draft is read from the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu])

  const syncHover = (clientX: number, clientY: number, target?: EventTarget | null) => {
    if (target instanceof Element && target.closest("[data-speed-split]")) return
    if (target instanceof Element && target.closest("[data-cut-delete]")) return
    if (hitKnot(clientX, clientY)) {
      setHoverCut((prev) => (prev == null ? prev : null))
      setSplitHover(false)
      return
    }
    const cutHit = hitCut(clientX)
    const next = cutHit >= 0 ? cutHit : null
    setHoverCut((prev) => (prev === next ? prev : next))
    const graph = graphRef.current?.getBoundingClientRect()
    const head = playheadRef.current?.getBoundingClientRect()
    const headX = head != null ? head.left + head.width / 2 : 0
    const onCut =
      graph != null &&
      durationMs > 0 &&
      draftRef.current.cuts.some((cut) => Math.abs(graph.left + (cut / durationMs) * graph.width - headX) <= 8)
    const near =
      graph != null &&
      head != null &&
      !onCut &&
      clientY >= graph.top &&
      clientY <= graph.bottom &&
      Math.abs(clientX - headX) <= 8
    setSplitHover((prev) => (prev === near ? prev : near))
  }

  const knotDragItems = (profile: CameraSpeedProfile, selection: KnotSelection[]) => {
    const sections = speedSectionStats(profile, durationMs)
    const items: KnotDragItem[] = []
    for (const sel of selection) {
      const section = sections[sel.segment]
      const point = section?.segment.knots[sel.knot]
      if (!section || !point) continue
      items.push({
        segment: sel.segment,
        knot: sel.knot,
        u: point.u,
        y: point.y,
        t0: section.t0,
        t1: section.t1,
      })
    }
    return items
  }

  const knotsInRect = (x0: number, y0: number, x1: number, y1: number) => {
    const bounds = graphRef.current?.getBoundingClientRect()
    const sections = speedSectionStats(draftRef.current, durationMs)
    if (!bounds || durationMs <= 0 || bounds.width <= 0) return []
    const box = marqueeClientRect(x0, y0, x1, y1, bounds)
    const hits: KnotSelection[] = []
    for (const section of sections) {
      const span = section.t1 - section.t0
      section.segment.knots.forEach((point, knotIndex) => {
        const px = bounds.left + ((section.t0 + point.u * span) / durationMs) * bounds.width
        const py = bounds.top + (speedToViewY(point.y) / 100) * bounds.height
        if (px >= box.left && px <= box.right && py >= box.top && py <= box.bottom) {
          hits.push({ segment: section.index, knot: knotIndex })
        }
      })
    }
    return hits
  }

  const showMarquee = (x0: number, y0: number, x1: number, y1: number) => {
    const bounds = graphRef.current?.getBoundingClientRect()
    if (!bounds) return
    setMarquee(marqueeStyle(x0, y0, x1, y1, bounds))
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.ctrlKey) return
    if (event.target instanceof Element && event.target.closest("[data-speed-split], [data-cut-delete]")) return
    event.stopPropagation()
    const point = locals(event.clientX, event.clientY)
    if (!point) return
    setMenu(null)
    setSplitHover(false)
    gestureRef.current = JSON.stringify(draftRef.current)
    const knotHit = hitKnot(event.clientX, event.clientY)
    if (knotHit) {
      setSelected(point.index)
      setHoverCut(null)
      const current = knotsRef.current
      const inGroup = current.some((item) => sameKnot(item, knotHit))
      const group = inGroup ? current : [knotHit]
      if (!inGroup) setKnots([knotHit])
      dragRef.current = {
        kind: "knots",
        originX: event.clientX,
        originY: event.clientY,
        base: draftRef.current,
        items: knotDragItems(draftRef.current, group),
      }
      event.currentTarget.setPointerCapture(event.pointerId)
      return
    }
    const cutHit = hitCut(event.clientX)
    if (cutHit >= 0) {
      setSelected(point.index)
      dragRef.current = { kind: "cut", index: cutHit }
      setCutDrag(cutHit)
      event.currentTarget.setPointerCapture(event.pointerId)
      return
    }
    const section = speedSectionStats(draftRef.current, durationMs)[point.index]
    const onLine = section ? hitsCurve(event.clientX, event.clientY, point.rect, section, durationMs) : false
    dragRef.current = {
      kind: "pending",
      index: point.index,
      u: point.u,
      onLine,
      clientX: event.clientX,
      clientY: event.clientY,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) {
      syncHover(event.clientX, event.clientY, event.target)
      return
    }
    if (drag.kind === "pending") {
      const moved = Math.hypot(event.clientX - drag.clientX, event.clientY - drag.clientY)
      if (moved < DRAG_SLOP_PX) return
      dragRef.current = {
        kind: "marquee",
        x0: drag.clientX,
        y0: drag.clientY,
        x1: event.clientX,
        y1: event.clientY,
      }
      showMarquee(drag.clientX, drag.clientY, event.clientX, event.clientY)
      return
    }
    if (drag.kind === "marquee") {
      drag.x1 = event.clientX
      drag.y1 = event.clientY
      showMarquee(drag.x0, drag.y0, event.clientX, event.clientY)
      return
    }
    const point = locals(event.clientX, event.clientY)
    if (!point) return
    if (drag.kind === "cut") {
      const next = moveSpeedCut(draftRef.current, durationMs, drag.index, point.ms)
      setDraft(next)
      draftRef.current = next
      return
    }
    const bounds = graphRef.current?.getBoundingClientRect()
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) return
    const deltaMs = ((event.clientX - drag.originX) / bounds.width) * durationMs
    const deltaY = ((drag.originY - event.clientY) / bounds.height) * SPEED_Y_MAX
    const moves = drag.items.map((item) => {
      const span = item.t1 - item.t0
      const originMs = item.t0 + item.u * span
      const u = span <= 1e-6 ? item.u : (originMs + deltaMs - item.t0) / span
      return { segment: item.segment, knot: item.knot, u, y: item.y + deltaY }
    })
    const moved = moveSpeedKnots(drag.base, moves)
    if (!moved) return
    setDraft(moved.profile)
    draftRef.current = moved.profile
    setKnots(moved.knots)
  }

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    setCutDrag(null)
    const rect = event.currentTarget.getBoundingClientRect()
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    if (inside) syncHover(event.clientX, event.clientY, event.target)
    else {
      setHoverCut(null)
      setSplitHover(false)
    }
    setMarquee(null)
    const drag = dragRef.current
    dragRef.current = null
    if (!drag) return
    if (drag.kind === "marquee") {
      setKnots(knotsInRect(drag.x0, drag.y0, drag.x1, drag.y1))
      return
    }
    if (drag.kind === "pending") {
      setSelected(drag.index)
      setKnots([])
      const point = locals(event.clientX, event.clientY)
      if (!drag.onLine) {
        if (point) onSeek(point.ms)
        return
      }
      const segment = draftRef.current.segments[drag.index]
      if (!segment) return
      const next = addSpeedKnot(draftRef.current, drag.index, drag.u, segmentSpeedAt(segment, drag.u))
      if (!next) return
      const added = next.segments[drag.index]?.knots ?? []
      let best = 0
      let bestDist = Number.POSITIVE_INFINITY
      added.forEach((knotPoint, index) => {
        const dist = Math.abs(knotPoint.u - drag.u)
        if (dist < bestDist) {
          best = index
          bestDist = dist
        }
      })
      setKnots([{ segment: drag.index, knot: best }])
      commit(next)
      return
    }
    if (JSON.stringify(draftRef.current) !== gestureRef.current) commit(draftRef.current)
  }

  const commitTimeEdit = (index: number, text: string) => {
    const originalMs = timeEdit?.index === index ? timeEdit.ms : null
    setTimeEdit(null)
    if (originalMs != null && text.trim() === formatTimecode(originalMs)) return
    const parsed = parseTimecode(text)
    if (parsed == null) return
    const cuts = draftRef.current.cuts
    if (index === cuts.length + 1) {
      const lastCut = cuts[cuts.length - 1] ?? 0
      const minMs = Math.max(MIN_SPLINE_DURATION_MS, lastCut + MIN_SPEED_CUT_GAP_MS)
      const nextMs = clamp(parsed, minMs, MAX_PATH_DURATION_MS)
      onDurationRef.current(nextMs)
      const repaired = normalizeSpeedProfile(draftRef.current, nextMs)
      if (repaired) commit(repaired)
      return
    }
    if (index <= 0 || index > cuts.length) return
    commit(moveSpeedCut(draftRef.current, durationMs, index - 1, parsed))
  }

  const msFromRuler = (clientX: number) => {
    const rect = rulerRef.current?.getBoundingClientRect()
    if (!rect || rect.width <= 0) return 0
    return clamp(((clientX - rect.left) / rect.width) * durationMs, 0, durationMs)
  }

  const timeIndexFromTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return null
    const node = target.closest("[data-time-index]")
    if (!node) return null
    const index = Number(node.getAttribute("data-time-index"))
    return Number.isInteger(index) && index > 0 ? index : null
  }

  const onRulerPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || timeEdit != null) return
    if (event.target instanceof Element && event.target.closest("input")) return
    event.preventDefault()
    rulerGestureRef.current = {
      x: event.clientX,
      scrubbing: false,
      editIndex: timeIndexFromTarget(event.target),
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onRulerPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = rulerGestureRef.current
    if (!gesture) return
    if (!gesture.scrubbing && Math.abs(event.clientX - gesture.x) <= RULER_DRAG_PX) return
    gesture.scrubbing = true
    onSeek(msFromRuler(event.clientX))
  }

  const onRulerPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = rulerGestureRef.current
    rulerGestureRef.current = null
    if (!gesture) return
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (gesture.scrubbing) {
      onSeek(msFromRuler(event.clientX))
      return
    }
    if (gesture.editIndex != null) {
      const ms = edges[gesture.editIndex]
      if (ms == null) return
      setTimeEdit({ index: gesture.editIndex, text: formatTimecode(ms), ms })
      return
    }
    onSeek(msFromRuler(event.clientX))
  }

  const curveLabel = (section: SpeedSectionStats) => {
    if (section.segment.preset === "pause") return t("locationsPage.scene3d.timeline.speedPause")
    if (section.segment.preset === "custom") return ""
    return t(`locationsPage.scene3d.timeline.speedPresets.${section.segment.preset}`)
  }

  return (
    <div ref={rootRef} className="relative flex flex-col select-none">
      <div
        ref={graphRef}
        className={cn("relative h-8 touch-none", (cutDrag != null || hoverCut != null) && "cursor-ew-resize")}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => {
          if (dragRef.current) return
          setHoverCut(null)
          setSplitHover(false)
        }}
        onDoubleClick={(event) => {
          if (hitKnot(event.clientX, event.clientY)) return
          const cut = hitCut(event.clientX)
          if (cut < 0) return
          event.preventDefault()
          event.stopPropagation()
          commit(removeSpeedCut(draftRef.current, durationMs, cut))
        }}
        onContextMenu={(event) => {
          event.preventDefault()
          event.stopPropagation()
          const point = locals(event.clientX, event.clientY)
          const root = rootRef.current?.getBoundingClientRect()
          if (!point || !root) return
          setSelected(point.index)
          setMenu({ index: point.index, x: event.clientX - root.left, y: event.clientY - root.top })
        }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-1/2 z-20 h-px -translate-y-1/2"
          style={{
            backgroundImage: `repeating-linear-gradient(to right, ${SPEED_GUIDE_GRAY} 0 3px, transparent 3px 6px)`,
          }}
          aria-hidden
        />
        {stats.map((section) => {
          const left = durationMs > 0 ? (section.t0 / durationMs) * 100 : 0
          const width = durationMs > 0 ? ((section.t1 - section.t0) / durationMs) * 100 : 100
          const points = sectionCurvePoints(section)
          const line = curvePath(points)
          const fill = curveFill(points)
          const pause = section.segment.preset === "pause"
          const span = section.t1 - section.t0
          const sectionW = durationMs > 0 ? (span / durationMs) * graphPx.w : graphPx.w
          const innerW = Math.max(sectionW - 2, 1)
          const innerH = Math.max(graphPx.h - 2, 1)
          const knotScaleX = sectionW > 2 && graphPx.h > 2 ? 100 / innerW : 0
          const knotScaleY = graphPx.h > 2 ? 100 / innerH : 0
          const atStart = section.index === 0
          const atEnd = section.index === stats.length - 1
          return (
            <div
              key={`${section.t0}-${section.index}`}
              className={cn(
                "absolute inset-y-0 overflow-hidden rounded-none border-y bg-ui-accent/10",
                atStart && "border-l",
                atEnd && "border-r",
                section.index === selected ? "z-10 border-ui-accent" : "border-ui-border",
              )}
              style={{ left: `${left}%`, width: `${Math.max(width, 0)}%` }}
            >
              {pause ? (
                <svg className="absolute inset-0 h-full w-full text-ui-text-muted/35" aria-hidden>
                  <defs>
                    <pattern
                      id={`speed-hatch-${section.index}`}
                      width="6"
                      height="6"
                      patternUnits="userSpaceOnUse"
                      patternTransform="rotate(45)"
                    >
                      <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="2" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill={`url(#speed-hatch-${section.index})`} />
                </svg>
              ) : (
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
                  <path d={fill} className="fill-ui-accent/20" />
                  <path
                    d={line}
                    className="fill-none stroke-ui-accent"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                  />
                  {section.segment.knots.length > 0 && knotScaleX > 0
                    ? section.segment.knots.map((point, knotIndex) => {
                        const active = knots.some((item) => item.segment === section.index && item.knot === knotIndex)
                        const cx = point.u * 100
                        const cy = speedToViewY(point.y)
                        return (
                          <circle
                            key={`${point.u}-${point.y}-${knotIndex}`}
                            r={active ? 3.5 : 2.5}
                            transform={`translate(${cx},${cy}) scale(${knotScaleX},${knotScaleY})`}
                            className="pointer-events-none fill-ui-accent"
                          />
                        )
                      })
                    : null}
                </svg>
              )}
              <span className="pointer-events-none absolute right-1 bottom-1 left-1 truncate text-[10px] text-ui-text-secondary">
                {curveLabel(section)}
              </span>
            </div>
          )
        })}
        {draft.cuts.map((cut, index) => (
          <div
            key={index}
            className="pointer-events-none absolute inset-y-0 z-20 w-3 -translate-x-1/2"
            style={{ left: `${durationMs > 0 ? (cut / durationMs) * 100 : 0}%` }}
          >
            <div
              className={cn(
                "absolute inset-y-0 left-1/2 -translate-x-1/2",
                cutDrag === index || hoverCut === index ? "w-1.5 bg-ui-accent/60" : "bg-transparent",
              )}
            />
            <div
              className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2"
              style={{
                backgroundImage: `repeating-linear-gradient(to bottom, ${SPEED_GUIDE_GRAY} 0 3px, transparent 3px 6px)`,
              }}
            />
            {hoverCut === index ? (
              <button
                type="button"
                data-cut-delete=""
                className={cn(
                  "pointer-events-auto absolute top-0 left-1/2 z-30 flex h-4 w-4 translate-x-1 cursor-pointer",
                  "items-center justify-center rounded-sm text-ui-text-muted hover:text-ui-text-primary",
                )}
                onPointerDown={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setHoverCut(null)
                  setKnots([])
                  setSelected((current) => (current != null && current > index ? current - 1 : current))
                  commit(removeSpeedCut(draftRef.current, durationMs, index))
                }}
              >
                <Trash2 className="h-2.5 w-2.5" />
              </button>
            ) : null}
          </div>
        ))}
        <div ref={playheadRef} className="absolute inset-y-0 z-30 w-px">
          <div className="pointer-events-none absolute inset-0 bg-ui-accent" />
          {splitHover ? (
            <button
              type="button"
              data-speed-split=""
              aria-label={t("locationsPage.scene3d.timeline.speedSplit")}
              className={cn(
                "pointer-events-auto absolute top-0 right-1/2 z-30 flex h-4 w-4 -translate-x-1 cursor-pointer",
                "items-center justify-center rounded-sm text-ui-text-muted hover:text-ui-text-primary",
              )}
              onPointerDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
                onSplit()
              }}
            >
              <Scissors className="h-2.5 w-2.5" />
            </button>
          ) : null}
        </div>
        {marquee ? (
          <div
            className={cn(
              "pointer-events-none absolute z-40 rounded-none",
              "border border-dashed border-ui-accent bg-ui-accent/15",
            )}
            style={{ left: marquee.left, top: marquee.top, width: marquee.width, height: marquee.height }}
          />
        ) : null}
      </div>
      <div
        ref={rulerRef}
        className="relative order-first mb-1 h-6 cursor-ew-resize touch-none"
        onPointerDown={onRulerPointerDown}
        onPointerMove={onRulerPointerMove}
        onPointerUp={onRulerPointerUp}
        onPointerCancel={onRulerPointerUp}
      >
        {edges.map((ms, index) => {
          const atStart = index === 0
          const atEnd = index === edges.length - 1
          const pct = durationMs > 0 ? (ms / durationMs) * 100 : 0
          const place = cn(
            "absolute font-mono text-[10px] text-ui-text-muted tabular-nums",
            atStart ? "left-0" : atEnd ? "right-0" : "-translate-x-1/2",
          )
          const placeStyle = atStart || atEnd ? undefined : { left: `${pct}%` }
          if (timeEdit?.index === index) {
            return (
              <input
                key={`${ms}-${index}`}
                value={timeEdit.text}
                autoFocus
                aria-label={formatTimecode(ms)}
                className={cn(
                  place,
                  "h-4 w-12 rounded-sm border border-ui-border bg-ui-bg-elevated px-0.5 outline-none",
                )}
                style={placeStyle}
                onChange={(event) =>
                  setTimeEdit((current) => (current == null ? current : { ...current, text: event.target.value }))
                }
                onPointerDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault()
                    event.currentTarget.blur()
                  } else if (event.key === "Escape") {
                    event.preventDefault()
                    cancelTimeEditRef.current = true
                    setTimeEdit(null)
                  }
                }}
                onBlur={(event) => {
                  if (cancelTimeEditRef.current) {
                    cancelTimeEditRef.current = false
                    return
                  }
                  commitTimeEdit(index, event.currentTarget.value)
                }}
              />
            )
          }
          if (atStart) {
            return (
              <span key={`${ms}-${index}`} className={cn(place, "cursor-text")} style={placeStyle}>
                {formatTimecode(ms)}
              </span>
            )
          }
          return (
            <button
              key={`${ms}-${index}`}
              type="button"
              data-time-index={index}
              className={cn(place, "cursor-text")}
              style={placeStyle}
            >
              {formatTimecode(ms)}
            </button>
          )
        })}
      </div>
      <div ref={anchorRef} className="absolute h-0 w-0" style={menu ? { left: menu.x, top: menu.y } : undefined} />
      {menu && menuStyle && portalRoot
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={menuStyle}
              className="min-w-36 rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-[var(--ui-shadow-md)]"
            >
              {SPEED_PRESETS.map((preset) => {
                const current = draft.segments[menu.index]?.preset === preset
                return (
                  <button
                    key={preset}
                    type="button"
                    role="menuitem"
                    className={MENU_ITEM}
                    onClick={() => {
                      const next = applySpeedPreset(draftRef.current, menu.index, preset as SpeedPresetName)
                      setMenu(null)
                      setKnots([])
                      commit(next)
                    }}
                  >
                    <SpeedPresetThumb preset={preset} />
                    {t(`locationsPage.scene3d.timeline.speedPresets.${preset}`)}
                    {current ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
                  </button>
                )
              })}
            </div>,
            portalRoot,
          )
        : null}
    </div>
  )
}
